<template>
	<nav
		class="nav-main nav-main--mobile-footer"
		@keydown.down.prevent="$emit('pass-focus', $event)"
	>
		<div class="flex gap-3">
			<ul class="nav-main__tabs flex gap-3 w-100">
				<li class="nav-main__tab" :class="isSelected('home') || isSelected('forms') ? 'nav-main__tab--selected' : null">
					<CIconButton
						text="Home"
						:to="homeRoute"
						:tabindex="isSelected('home') ? '-1' : '0'"
						icon="home"
						:intense="false"
					/>
				</li>
				<li class="nav-main__tab" :class="isSelected('task-dashboard') ? 'nav-main__tab--selected' : null">
					<CIconButton
						icon="tasks"
						:to="taskRoute"
						:intense="false"
						:tabindex="isSelected('task-dashboard') ? '-1' : '0'"
						@click="handleTasksButtonClick"
					>
						<div class="relative">
							Tasks
							<CBadge v-if="isPaidPlan && taskCount > 0" :text="getAbbreviatedNumber(taskCount)" type="alert" class="nav-main--mobile-footer__task absolute" />
						</div>
					</CIconButton>
				</li>
				<li v-if="isOrgAdmin && showCognitoPay" class="nav-main__tab">
					<CIconButton
						text="Payment"
						:to="hasOnboardedCognitoPayAccount ? cognitoPayRoute : null"
						icon="credit-card"
						:intense="false"
						:data-cf-id="hasOnboardedCognitoPayAccount ? 'cognito-pay.nav-dashboard' : 'cognito-pay.nav-onboarding'"
						@click="$emit('payment-clicked')"
					/>
				</li>
				<li v-if="isOrgAdmin" class="nav-main__tab">
					<CIconButton text="Settings" :to="settingsRoute" icon="settings" :intense="false" />
				</li>
			</ul>
		</div>
		<CUpsellDialog
			:show="showTasksUpsellDialog"
			feature="tasks"
			:org-code="orgCode"
			:is-org-owner="isOrgOwner"
			@close="showTasksUpsellDialog = false"
		/>
	</nav>
</template>

<script lang="ts">
	import CIconButton from 'src/components/interaction/IconButton.vue';
	import CBadge from 'src/components/info/Badge.vue';
	import CUpsellDialog from 'src/components/dialogs/UpsellDialog.vue';
	import { getAbbreviatedNumber } from '@cognitoforms/utils/utilities';
	import { computed, ref } from 'vue';
	import { useRoute } from 'vue-router/composables';
	import session from '@cognitoforms/api/services/session';

	export default {
		name: 'CNavMain',
		components: {
			CIconButton,
			CBadge,
			CUpsellDialog
		},
		props: {
			isOrgOwner: { type: Boolean, required: true },
			isOrgAdmin: { type: Boolean, required: true },
			orgCode: { type: String, required: true },
			isPaidPlan: { type: Boolean, required: true },
			canUseTasks: { type: Boolean, required: true },
			taskCount: { type: Number, required: true },
			hasOnboardedCognitoPayAccount: { type: Boolean, default: false },
			hasIncompleteCognitoPayAccount: { type: Boolean, default: false }
		},
		emits: ['pass-focus', 'payment-clicked'],
		setup(props) {
			const route = useRoute();
			const homeRoute = computed(() => `/${props.orgCode}/home`);
			const settingsRoute = computed(() => `/${props.orgCode}/settings/general`);

			const showCognitoPay = computed(() => {
				return session.value?.enabledFeatures?.CognitoPay && (props.hasOnboardedCognitoPayAccount || props.hasIncompleteCognitoPayAccount);
			});
			const cognitoPayRoute = computed(() => `/${props.orgCode}/payment/analytics`);

			const taskRoute = computed(() => (props.canUseTasks) ? `/${props.orgCode}/tasks` : '');

			const showTasksUpsellDialog = ref(false);
			const handleTasksButtonClick = () => {
				if (!taskRoute.value)
					showTasksUpsellDialog.value = true;
			};

			const isSelected = (name) => {
				return route.name === name;
			};

			return {
				handleTasksButtonClick,
				getAbbreviatedNumber,
				homeRoute,
				settingsRoute,
				showCognitoPay,
				cognitoPayRoute,
				showTasksUpsellDialog,
				taskRoute,
				isSelected
			};
		}
	};
</script>

<style lang="scss">
.nav-main {
	@include b-down($b9) {
		// tighter space here than elsewhere
		--space__touch-target: 10px;
	}

	&--mobile-footer {
		width: 100%;
		padding-bottom: env(safe-area-inset-bottom, 0);

		:root:root &__task {
			bottom: 38px;
			left: 23px;
			min-width: 16px;
			height: 16px;
			padding: 0 .3em;
			font-size: 9px;
		}

		.nav-main__tabs {
			display: flex;
			justify-content: space-around;

			.nav-main__tab {
				width: 100%;

				div {

					:root & {
						display: inline-flex;
					}
					justify-content: center;
					width: 100%;

					button {
						width: 100%;
					}
				}
			}

			.icon {
				width: 25px;
				height: 25px;
				color: var(--icon-intense);
			}
		}

		.i-button {
			flex-direction: column;
			gap: 0;
			width: 100%;
		}
	}
}
</style>